// @ts-check

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  IndexTable,
  Button,
  Popover,
  ActionList,
  ButtonGroup,
  Text,
  InlineStack,
  useBreakpoints,
  BlockStack,
  Link,
  Tooltip,
} from "@shopify/polaris";
import { FilterIcon, MenuHorizontalIcon } from "@shopify/polaris-icons";

import { useFormatCurrency } from "hooks/use-format-currency";

import { UTM_FIELDS } from "utils/utm-sets";

const generateUtmLink = (domain, utmParams) => {
  let parsedUrl;
  try {
    parsedUrl = new URL(domain);
  } catch (error) {
    parsedUrl = new URL("https://" + domain);
  }

  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

  for (const key of utmKeys) {
    if (utmParams[key]) {
      parsedUrl.searchParams.set(key, utmParams[key]);
    }
  }

  return parsedUrl.toString();
};

const Actions = ({ utmSet }) => {
  const shopify = useAppBridge();
  const navigate = useNavigate();

  const [rowPopoverActive, setRowPopoverActive] = useState(false);

  const toggleRowPopoverActive = useCallback(
    () => setRowPopoverActive((popoverActive) => !popoverActive),
    []
  );

  const copyToClipboard = (text) => {
    const tempInput = document.createElement("input");
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);
  };

  const handleViewUtmSet = () => {
    const params = {};

    for (const utmField of UTM_FIELDS) {
      params[utmField.key] = utmSet[utmField.key] || "";
    }

    navigate(`/utm-sets/view?${new URLSearchParams(params).toString()}`);
  };

  const utmUrl = generateUtmLink(utmSet.domain, utmSet);

  return (
    <ButtonGroup variant="segmented">
      <Button onClick={handleViewUtmSet}>
        View UTM Set
      </Button>
      <Popover
        active={rowPopoverActive}
        activator={
          <Button
            icon={MenuHorizontalIcon}
            onClick={(e) => {
              e.stopPropagation();
              toggleRowPopoverActive();
            }}
          />
        }
        autofocusTarget="first-node"
        onClose={toggleRowPopoverActive}
      >
        <ActionList
          actionRole="menuitem"
          items={[
            {
              content: "Copy UTM Snippet",
              onAction: () => {
                copyToClipboard(utmUrl.split("?")[1]);
                toggleRowPopoverActive();
                shopify.toast.show("Copied to Clipboard");
              },
            },
          ]}
        />
      </Popover>
    </ButtonGroup>
  )
};

const MobileUtmSetsTableRow = ({
  id,
  position,
  selected,
  utmSet,
}) => {
  const formatCurrency = useFormatCurrency();

  const formattedOrders = `${utmSet.total_orders} order${utmSet.total_orders !== 1 ? "s" : ""}`;

  return (
    <IndexTable.Row id={id} position={position} selected={selected}>
      <div style={{ padding: "12px 16px", width: "100%" }}>
        <BlockStack gap="300">
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              {formattedOrders} • {formatCurrency(utmSet.total_revenue)}
            </Text>

            {UTM_FIELDS.map(({ label, key }) => (
              <BlockStack key={key}>
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  {label}
                </Text>

                <Text as="p" variant="bodyMd">
                  {utmSet[key] || "-"}
                </Text>
              </BlockStack>
            ))}
          </BlockStack>

          <div>
            <Actions utmSet={utmSet} />
          </div>
        </BlockStack>
      </div>
    </IndexTable.Row>
  );
};

const LINKABLE_VALUES = [
  {
    key: "utm_source",
    createHref: (value) => `/reports/source/${encodeURIComponent(value)}`,
  },
  {
    key: "utm_medium",
    createHref: (value) => `/reports/medium/${encodeURIComponent(value)}`,
  },
  {
    key: "utm_campaign",
    createHref: (value) => `/reports/campaign/${encodeURIComponent(value)}`,
  },
];

const TRUNCATE_LENGTH = 40;

export const UtmSetsTableRow = ({
  id,
  columns,
  sortedValues,
  utmSet,
  index,
  selected,
  setFilterValue,
}) => {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();

  if (useBreakpoints().smDown) {
    return (
      <MobileUtmSetsTableRow
        id={id}
        position={index}
        selected={selected}
        utmSet={utmSet}
      />
    )
  }

  const utmColumns = columns
    .filter((col) => col !== "total_revenue" && col !== "total_orders" && col !== "actions")
    .slice()
    .sort((a, b) => sortedValues.indexOf(a) - sortedValues.indexOf(b))
    .map((col) => {
      let value = utmSet[col] || "-";
      const linkableValue = LINKABLE_VALUES.find(({ key }) => key === col);
      const shouldTruncate = utmSet[col] && utmSet[col].length > TRUNCATE_LENGTH;

      if (shouldTruncate) {
        value = (
          <Text as="span">
            {value.substring(0, TRUNCATE_LENGTH).trim()}...
          </Text>
        );
      }

      let filterButton;
      if (utmSet[col]) {
        filterButton = (
          <div className="filter-button-container">
            <Tooltip content="Filter by this value" dismissOnMouseOut>
              <Button
                size="micro"
                icon={FilterIcon}
                onClick={() => {
                  setFilterValue(col, utmSet[col]);
                }}
              />
            </Tooltip>
          </div>
        );

        const linkableValue = LINKABLE_VALUES.find(({ key }) => key === col);

        if (linkableValue) {
          const handleClick = () => {
            navigate(linkableValue.createHref(utmSet[col]));
          }

          value = (
            <Link
              onClick={handleClick}
              removeUnderline
            >
              {value}
            </Link>
          );
        }
      }

      if (utmSet[col] && linkableValue) {
        const handleClick = () => {
          navigate(linkableValue.createHref(utmSet[col]));
        }

        value = (
          <Link
            onClick={handleClick}
            removeUnderline
          >
            {value}
          </Link>
        );
      }

      let truncateWrapped = value;
      if (shouldTruncate) {
        truncateWrapped = (
          <Tooltip content={utmSet[col]} dismissOnMouseOut>
            {value}
          </Tooltip>
        )
      }

      return (
        <IndexTable.Cell key={col}>
          <div className="utm-table-cell">
            {truncateWrapped}
            {filterButton}
          </div>
        </IndexTable.Cell>
      )
    });

  return (
    <IndexTable.Row id={id} position={index} selected={selected}>
      {utmColumns}
      {columns.includes("total_orders") ? (
        <IndexTable.Cell key="total_orders">
          <Text variant="bodyMd" as="span" alignment="end" numeric>
            {utmSet.total_orders}
          </Text>
        </IndexTable.Cell>
      ) : null}
      {columns.includes("total_revenue") ? (
        <IndexTable.Cell key="total_revenue">
          <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>
            {formatCurrency(utmSet.total_revenue)}
          </Text>
        </IndexTable.Cell>
      ) : null}
      <IndexTable.Cell key="actions">
        <InlineStack align="end">
          <Actions utmSet={utmSet} />
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};
